<!--
* @Author: AI Assistant
* @Date: 2026-01-12
 * @LastEditors: 冯伟绩 fengweiji@wekoi.cn
 * @LastEditTime: 2026-02-10 16:14:37
* @Description: API 页面 - 提供 API 文档和相关功能
* @FilePath: /weryai-web/pages/api/index.vue
-->
<template>
    <div class="api-page-wrapper min-h-screen bg-black text-white">
        <!-- This tab must remain visible while the app header is sticky. -->
        <header
            class="api-header fixed left-0 right-0 z-[48] h-[57px] max-md:h-auto bg-black/90 backdrop-blur-xl border-b border-[#18181B]">
            <div class="h-full overflow-x-auto overflow-y-hidden max-md:overflow-x-scroll">
                <div class="flex h-full gap-[28px] px-7 max-md:gap-[14px] max-md:px-[14px] max-md:h-[42px]">
                    <div v-for="tab in tabs" :key="tab.key" :class="[
                        'flex items-center gap-[7px] h-14 border-b-2 transition-colors flex-shrink-0 max-md:h-[34px] max-md:px-[3.5px] cursor-pointer',
                        isActiveTab(tab.key)
                            ? 'border-[#99F768] text-[#99F768]'
                            : 'border-transparent text-[#71717A] hover:text-white',
                    ]" @click="handleTabClick(tab)">
                        <i :class="[
                            'w-4 h-4 flex-shrink-0',
                            tab.iconClass,
                            isActiveTab(tab.key) ? 'active' : '',
                        ]"></i>
                        <span :class="[
                            'font-semibold text-[12.3px] leading-[1.42] whitespace-nowrap max-md:text-[11px] max-md:leading-[1.5em]',
                            tab.key === 'webhooks'
                                ? 'font-bold'
                                : 'font-semibold',
                        ]">
                            {{ tab.name }}
                        </span>
                    </div>
                </div>
            </div>
        </header>

        <!-- 内容区域 - 使用 Transition 添加动画效果 -->
        <main class="api-main-content max-w-[1400px] mx-auto px-5 max-md:px-0 py-10 max-md:py-0">
            <Transition name="page" mode="out-in">
                <div :key="$route.fullPath">
                    <NuxtPage />
                </div>
            </Transition>
        </main>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead, navigateTo } from '#app'
import { useLocalePath } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMailto } from '~/composables/useMailto'
import { supportedLanguages } from '~/locales'

const route = useRoute()
const localePath = useLocalePath()
const { t } = useI18n()
const { openMailto } = useMailto()

// 从公共配置中获取支持的语言列表
const SUPPORTED_LOCALES = supportedLanguages

// 去掉路径中的语言前缀
const stripLocalePrefix = (path: string) => {
    const match = path.match(/^\/([a-z]{2})(\/|$)/)
    const lang = match?.[1]
    if (lang && SUPPORTED_LOCALES.includes(lang as any)) {
        return path.replace(new RegExp(`^/${lang}`), '') || '/'
    }
    return path
}

// Tab 配置
interface TabItem {
    key: string
    name: string
    iconClass: string
    path: string
    isExternal?: boolean
    externalUrl?: string
}

// 使用 computed 让 tabs 响应式更新
const tabs = computed<TabItem[]>(() => [
    {
        key: 'discovery',
        name: t('api.discovery'),
        iconClass: 'icon-discovery',
        path: '/api/discovery',
    },
    {
        key: 'skills',
        name: t('api.skills'),
        iconClass: 'icon-skills',
        path: '/api/skills',
    },
    {
        key: 'docs',
        name: t('api.docs'),
        iconClass: 'icon-docs',
        path: '/api/docs',
        isExternal: true,
        externalUrl: 'https://docs.weryai.com/',
    },
    {
        key: 'keys',
        name: t('api.apiKeys'),
        iconClass: 'icon-keys',
        path: '/api/keys',
    },
    {
        key: 'history',
        name: t('api.history'),
        iconClass: 'icon-history',
        path: '/api/history',
    },
    {
        key: 'pricing',
        name: t('api.pricing'),
        iconClass: 'icon-pricing',
        path: '/api/pricing',
    },
    {
        key: 'contact',
        name: t('api.contact'),
        iconClass: 'icon-contact',
        path: '/api/contact',
    },
])

// 获取当前路径（去掉语言前缀后，响应式）
const currentPath = computed(() => stripLocalePrefix(route.path))

// 为每个 tab 创建激活状态的计算属性（确保响应式）
const activeTabStates = computed(() => {
    const path = currentPath.value
    const states: Record<string, boolean> = {}

    tabs.value.forEach(tab => {
        if (!tab.path) {
            states[tab.key] = false
            return
        }

        // 如果是 pricing，需要匹配 /api/pricing 开头的路径（包括子路由）
        if (tab.key === 'pricing') {
            states[tab.key] = path.startsWith('/api/pricing')
        } else {
            // 其他 tab 精确匹配路径
            states[tab.key] =
                path === tab.path || path.startsWith(tab.path + '/')
        }
    })

    return states
})

// 判断当前 tab 是否激活（从计算属性中获取）
const isActiveTab = (key: string) => {
    return activeTabStates.value[key] || false
}

// 处理 tab 点击
const handleTabClick = (tab: TabItem) => {
    if (tab.isExternal && tab.externalUrl) {
        // 打开新标签页或外部链接
        if (tab.externalUrl.startsWith('mailto:')) {
            void openMailto(tab.externalUrl)
        } else {
            window.open(tab.externalUrl, '_blank')
        }
    } else if (tab.path) {
        // 路由跳转（自动添加当前语言前缀）
        navigateTo(localePath(tab.path))
    }
}

// SEO 设置 - 使用 computed 让标题和描述响应式更新
useHead({
    title: computed(() => t('api.pageTitle')),
    meta: [
        {
            name: 'description',
            content: computed(() => t('api.pageDescription')),
        },
    ],
})
</script>

<style scoped lang="less">
// API 页面容器样式
.api-page-wrapper {
    position: relative;
}

// This fixed exception uses the app-level header contract, never a pixel offset.
.api-header {
    top: var(--app-header-bottom);
}

// Reserve only this page's own fixed tab; Header space is already in normal flow.
.api-main-content {
    --api-tab-height: 57px;
    padding-top: var(--api-tab-height);

    @media (max-width: 768px) {
        --api-tab-height: 42px;
    }
}

// 图标基础样式
i[class^='icon-'] {
    display: inline-block;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
}

// Discovery 图标 - 地球仪图标
.icon-discovery {
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.00016 14.6663C11.6821 14.6663 14.6668 11.6816 14.6668 7.99967C14.6668 4.31778 11.6821 1.33301 8.00016 1.33301C4.31826 1.33301 1.3335 4.31778 1.3335 7.99967C1.3335 11.6816 4.31826 14.6663 8.00016 14.6663Z' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M8.00016 1.33301C6.28832 3.13044 5.3335 5.5175 5.3335 7.99967C5.3335 10.4818 6.28832 12.8689 8.00016 14.6663C9.71201 12.8689 10.6668 10.4818 10.6668 7.99967C10.6668 5.5175 9.71201 3.13044 8.00016 1.33301Z' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1.3335 8H14.6668' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

    &.active {
        background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.00016 14.6663C11.6821 14.6663 14.6668 11.6816 14.6668 7.99967C14.6668 4.31778 11.6821 1.33301 8.00016 1.33301C4.31826 1.33301 1.3335 4.31778 1.3335 7.99967C1.3335 11.6816 4.31826 14.6663 8.00016 14.6663Z' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M8.00016 1.33301C6.28832 3.13044 5.3335 5.5175 5.3335 7.99967C5.3335 10.4818 6.28832 12.8689 8.00016 14.6663C9.71201 12.8689 10.6668 10.4818 10.6668 7.99967C10.6668 5.5175 9.71201 3.13044 8.00016 1.33301Z' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1.3335 8H14.6668' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
}

// Docs 图标
.icon-docs {
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 4.66699V14.0003' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2.00016 12C1.82335 12 1.65378 11.9298 1.52876 11.8047C1.40373 11.6797 1.3335 11.5101 1.3335 11.3333V2.66667C1.3335 2.48986 1.40373 2.32029 1.52876 2.19526C1.65378 2.07024 1.82335 2 2.00016 2H5.3335C6.04074 2 6.71902 2.28095 7.21911 2.78105C7.71921 3.28115 8.00016 3.95942 8.00016 4.66667C8.00016 3.95942 8.28111 3.28115 8.78121 2.78105C9.28131 2.28095 9.95959 2 10.6668 2H14.0002C14.177 2 14.3465 2.07024 14.4716 2.19526C14.5966 2.32029 14.6668 2.48986 14.6668 2.66667V11.3333C14.6668 11.5101 14.5966 11.6797 14.4716 11.8047C14.3465 11.9298 14.177 12 14.0002 12H10.0002C9.46973 12 8.96102 12.2107 8.58595 12.5858C8.21088 12.9609 8.00016 13.4696 8.00016 14C8.00016 13.4696 7.78945 12.9609 7.41438 12.5858C7.0393 12.2107 6.5306 12 6.00016 12H2.00016Z' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

    &.active {
        background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 4.66699V14.0003' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2.00016 12C1.82335 12 1.65378 11.9298 1.52876 11.8047C1.40373 11.6797 1.3335 11.5101 1.3335 11.3333V2.66667C1.3335 2.48986 1.40373 2.32029 1.52876 2.19526C1.65378 2.07024 1.82335 2 2.00016 2H5.3335C6.04074 2 6.71902 2.28095 7.21911 2.78105C7.71921 3.28115 8.00016 3.95942 8.00016 4.66667C8.00016 3.95942 8.28111 3.28115 8.78121 2.78105C9.28131 2.28095 9.95959 2 10.6668 2H14.0002C14.177 2 14.3465 2.07024 14.4716 2.19526C14.5966 2.32029 14.6668 2.48986 14.6668 2.66667V11.3333C14.6668 11.5101 14.5966 11.6797 14.4716 11.8047C14.3465 11.9298 14.177 12 14.0002 12H10.0002C9.46973 12 8.96102 12.2107 8.58595 12.5858C8.21088 12.9609 8.00016 13.4696 8.00016 14C8.00016 13.4696 7.78945 12.9609 7.41438 12.5858C7.0393 12.2107 6.5306 12 6.00016 12H2.00016Z' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
}

// Keys 图标
.icon-keys {
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.3335 5.00033L11.8668 6.53366C11.9914 6.65581 12.159 6.72423 12.3335 6.72423C12.508 6.72423 12.6755 6.65581 12.8002 6.53366L14.2002 5.13366C14.3223 5.00904 14.3907 4.84149 14.3907 4.66699C14.3907 4.49249 14.3223 4.32495 14.2002 4.20033L12.6668 2.66699' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M14.0001 1.33301L7.6001 7.73301' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M5.00016 14.0003C7.02521 14.0003 8.66683 12.3587 8.66683 10.3337C8.66683 8.30861 7.02521 6.66699 5.00016 6.66699C2.97512 6.66699 1.3335 8.30861 1.3335 10.3337C1.3335 12.3587 2.97512 14.0003 5.00016 14.0003Z' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

    &.active {
        background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.3335 5.00033L11.8668 6.53366C11.9914 6.65581 12.159 6.72423 12.3335 6.72423C12.508 6.72423 12.6755 6.65581 12.8002 6.53366L14.2002 5.13366C14.3223 5.00904 14.3907 4.84149 14.3907 4.66699C14.3907 4.49249 14.3223 4.32495 14.2002 4.20033L12.6668 2.66699' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M14.0001 1.33301L7.6001 7.73301' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M5.00016 14.0003C7.02521 14.0003 8.66683 12.3587 8.66683 10.3337C8.66683 8.30861 7.02521 6.66699 5.00016 6.66699C2.97512 6.66699 1.3335 8.30861 1.3335 10.3337C1.3335 12.3587 2.97512 14.0003 5.00016 14.0003Z' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
}

// History 图标
.icon-history {
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 8C2 9.18669 2.35189 10.3467 3.01118 11.3334C3.67047 12.3201 4.60754 13.0892 5.7039 13.5433C6.80026 13.9974 8.00666 14.1162 9.17054 13.8847C10.3344 13.6532 11.4035 13.0818 12.2426 12.2426C13.0818 11.4035 13.6532 10.3344 13.8847 9.17054C14.1162 8.00666 13.9974 6.80026 13.5433 5.7039C13.0892 4.60754 12.3201 3.67047 11.3334 3.01118C10.3467 2.35189 9.18669 2 8 2C6.32263 2.00631 4.71265 2.66082 3.50667 3.82667L2 5.33333' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2 2V5.33333H5.33333' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M8 4.66699V8.00033L10.6667 9.33366' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

    &.active {
        background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 8C2 9.18669 2.35189 10.3467 3.01118 11.3334C3.67047 12.3201 4.60754 13.0892 5.7039 13.5433C6.80026 13.9974 8.00666 14.1162 9.17054 13.8847C10.3344 13.6532 11.4035 13.0818 12.2426 12.2426C13.0818 11.4035 13.6532 10.3344 13.8847 9.17054C14.1162 8.00666 13.9974 6.80026 13.5433 5.7039C13.0892 4.60754 12.3201 3.67047 11.3334 3.01118C10.3467 2.35189 9.18669 2 8 2C6.32263 2.00631 4.71265 2.66082 3.50667 3.82667L2 5.33333' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2 2V5.33333H5.33333' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M8 4.66699V8.00033L10.6667 9.33366' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
}

// Pricing 图标
.icon-pricing {
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3335 3.33301H2.66683C1.93045 3.33301 1.3335 3.92996 1.3335 4.66634V11.333C1.3335 12.0694 1.93045 12.6663 2.66683 12.6663H13.3335C14.0699 12.6663 14.6668 12.0694 14.6668 11.333V4.66634C14.6668 3.92996 14.0699 3.33301 13.3335 3.33301Z' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1.3335 6.66699H14.6668' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

    &.active {
        background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3335 3.33301H2.66683C1.93045 3.33301 1.3335 3.92996 1.3335 4.66634V11.333C1.3335 12.0694 1.93045 12.6663 2.66683 12.6663H13.3335C14.0699 12.6663 14.6668 12.0694 14.6668 11.333V4.66634C14.6668 3.92996 14.0699 3.33301 13.3335 3.33301Z' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1.3335 6.66699H14.6668' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
}

// Contact 图标
.icon-contact {
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.6668 4.66699L8.67283 8.48499C8.46943 8.60313 8.23839 8.66536 8.00316 8.66536C7.76794 8.66536 7.5369 8.60313 7.3335 8.48499L1.3335 4.66699' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M13.3335 2.66699H2.66683C1.93045 2.66699 1.3335 3.26395 1.3335 4.00033V12.0003C1.3335 12.7367 1.93045 13.3337 2.66683 13.3337H13.3335C14.0699 13.3337 14.6668 12.7367 14.6668 12.0003V4.00033C14.6668 3.26395 14.0699 2.66699 13.3335 2.66699Z' stroke='%2371717A' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");

    &.active {
        background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.6668 4.66699L8.67283 8.48499C8.46943 8.60313 8.23839 8.66536 8.00316 8.66536C7.76794 8.66536 7.5369 8.60313 7.3335 8.48499L1.3335 4.66699' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M13.3335 2.66699H2.66683C1.93045 2.66699 1.3335 3.26395 1.3335 4.00033V12.0003C1.3335 12.7367 1.93045 13.3337 2.66683 13.3337H13.3335C14.0699 13.3337 14.6668 12.7367 14.6668 12.0003V4.00033C14.6668 3.26395 14.0699 2.66699 13.3335 2.66699Z' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
}

// skills 图标
.icon-skills {
    background-image: url("../assets/images/api/api-skill-icon2.png");
    // background-position: 
    // ;
    background-size: contain;

    &.active {
        // background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.6668 4.66699L8.67283 8.48499C8.46943 8.60313 8.23839 8.66536 8.00316 8.66536C7.76794 8.66536 7.5369 8.60313 7.3335 8.48499L1.3335 4.66699' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M13.3335 2.66699H2.66683C1.93045 2.66699 1.3335 3.26395 1.3335 4.00033V12.0003C1.3335 12.7367 1.93045 13.3337 2.66683 13.3337H13.3335C14.0699 13.3337 14.6668 12.7367 14.6668 12.0003V4.00033C14.6668 3.26395 14.0699 2.66699 13.3335 2.66699Z' stroke='%2399F768' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-image: url("../assets/images/api/api-skill-icon1.png");
    }
}
// 页面切换动画
.page-enter-active,
.page-leave-active {
    transition: all 0.3s ease;
}

.page-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.page-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
